import React, { Component, Fragment } from "react";
import PropTypes from "prop-types";
import client from "common/admin/client";
import { transformApiError } from "common/api";
import { CircularProgress, Snackbar } from "@mui/material";
import { find, pick } from "lodash";
import SuperadminOfferReview from "./SuperadminOfferReview";
import { AppValues, TARGET_PREFIXES } from "../../../common/constants";

const updateFields = [
  "short_description",
  "long_description",
  "custom_values",
  "legal",
  "start_at",
  "end_at",
  "target",
  "quantity",
  "offer_template",
  "event_id",
  "building_group_id",
];

class SuperadminOfferReviewContainer extends Component {
  state = {
    loaded: false,
    offer: null,
    city: null,
    templates: [],
    error: null,
    isHilo: true,
    eventTargets: [],
    groupTargets: [],
  };

  async componentDidMount() {
    const { offerId } = this.props.match.params;

    try {
      const offer = await client.get(`offers/${offerId}`);

      let resource, resourceId;
      if (offer.building_id) {
        resource = "buildings";
        resourceId = offer.building_id;
      } else if (offer.business_id) {
        resource = "businesses";
        resourceId = offer.business_id;
      } else {
        throw new Error("Could not determine resource for offer");
      }

      const resourceResponse = await client.get(`${resource}/${resourceId}`);
      let is_hilo = true;

      if (resourceResponse && resourceResponse?.building_id) {
        const building = !offer.building_id
          ? await client.get(`buildings/${resourceResponse.building_id}`)
          : resourceResponse;
        is_hilo = building.is_hilo;
      }

      const buildingId = offer.building_id || resourceResponse?.building_id;

      if (buildingId) {
        try {
          let eventTargets = [];
          let groupTargets = [];

          if (offer && offer.offer_template === "event") {
            const activeNotifications = await client.get(
              `buildings/${buildingId}/notifications`,
            );
            if (activeNotifications && activeNotifications.length > 0) {
              eventTargets = activeNotifications
                .filter((x) => x.event_id && !x.is_contest)
                .map((notification) => ({
                  value: notification.event_id,
                  label: notification.title,
                }));
            }
          }

          const activeBuildingGroups = await client.get(
            `buildings/${buildingId}/groups`,
          );
          if (
            activeBuildingGroups &&
            activeBuildingGroups.groups &&
            activeBuildingGroups.groups.length > 0
          ) {
            groupTargets = activeBuildingGroups.groups.map((group) => ({
              value: `${TARGET_PREFIXES.group}${group.group_id}`,
              label: group.group_name || group.group_name_fr,
            }));
          }

          this.setState({ eventTargets, groupTargets });
        } catch (error) {
          console.error("Failed to fetch event/group targets:", error);
        }
      }

      const community = await client.get(
        `communities/${resourceResponse.community_id}`,
      );
      const city = await client.get(`cities/${community.city_id}`);
      const templates = await client.get("offer_templates");
      this.setState({
        offer,
        city,
        templates,
        loaded: true,
        isHilo: is_hilo,
      });
    } catch (error) {
      this.setState({ error, loaded: true });
    }
  }

  _handleApprove = async () => {
    const { offerId } = this.props.match.params;
    try {
      const offer = await client.post(
        `offers/${offerId}/approve`,
        this.state.isHilo === false
          ? { header: AppValues.okktoRequestHeader }
          : undefined,
      );
      this.setState({ offer });
    } catch (error) {
      this.setState({ error });
    }
  };

  _handleDelete = async () => {
    const { offerId } = this.props.match.params;
    try {
      await client.del(`offers/${offerId}`);
      this.props.history.push("/offers");
    } catch (e) {
      const error = transformApiError(e);
      if (error.empty()) {
        this.setState({
          error: { message: "An unknown error occurred." },
        });
      } else {
        const err = error.array(0);
        this.setState({ error: { message: err.msg } });
      }
    }
  };

  _handleChangeRequest = async (values) => {
    const { offerId } = this.props.match.params;
    const body = {
      ...pick(values, updateFields),
      legal: values.defaultTerms ? null : values.legal,
      offer_template: values.offer_template.id,
    };

    try {
      let offer = await client.patch(`offers/${offerId}`, { body });

      if (values.hero) {
        const response = await client.postFile(
          "offers",
          offerId,
          "hero",
          values.hero,
        );

        await client.patch(`offers/${offerId}`, {
          body: {
            hero_url: response.url,
          },
        });
      }

      // if (offer.status !== "approved") {
      //   offer = await client.post(
      //     `offers/${offerId}/change_request`,
      //     this.state.isHilo == false
      //       ? { header: AppValues.okktoRequestHeader }
      //       : undefined
      //   );
      // }

      this.setState({ offer });
    } catch (error) {
      this.setState({ error });
    }
  };

  _clearError = () => {
    this.setState({ error: null });
  };

  _downloadRedemptions = async () => {
    const { offerId } = this.props.match.params;
    let { offer } = this.state;
    try {
      const { noRedemptions, csvUrl } = await client.get(
        `offers/${offerId}/download_redemptions`,
      );
      const updatedOffer = Object.assign({}, offer, {
        noRedemptions,
        downloadRedemptionsLink: csvUrl,
      });
      this.setState({ offer: updatedOffer });
    } catch (error) {
      this.setState({ error });
    }
  };

  render() {
    const { offer, city, templates, loaded, error, eventTargets, groupTargets } =
      this.state;

    if (!loaded) {
      return <CircularProgress />;
    }

    const template = find(templates, { id: offer.offer_template });

    return (
      <Fragment>
        <SuperadminOfferReview
          offer={offer}
          timezone={city.timezone_name}
          template={template}
          handleApprove={this._handleApprove}
          handleChangeRequest={this._handleChangeRequest}
          handleDelete={this._handleDelete}
          downloadRedemptions={this._downloadRedemptions}
          eventTargets={eventTargets}
          groupTargets={groupTargets}
        />
        {error ? (
          <Snackbar
            open
            autoHideDuration={4000}
            onClose={this._clearError}
            message={<span>Error: {error.message}</span>}
          />
        ) : null}
      </Fragment>
    );
  }
}

SuperadminOfferReviewContainer.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      offerId: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
};

export default SuperadminOfferReviewContainer;
